import React, { useEffect } from 'react';
import { useI18n } from '../i18n';
import { slugify, generateTocFromSections } from '../lib/slugger';
import { formatLastUpdated } from '../lib/seo';
import LanguageToggle from './LanguageToggle';

interface LegalSection {
  heading: string;
  body: string[];
}

interface LegalLayoutProps {
  title: string;
  intro: string;
  sections: LegalSection[];
  metaTitle: string;
  metaDescription: string;
  tocTitle: string;
  lastUpdatedText: string;
}

export default function LegalLayout({
  title,
  intro,
  sections,
  metaTitle,
  metaDescription,
  tocTitle,
  lastUpdatedText
}: LegalLayoutProps) {
  const { lang, translations } = useI18n();
  const isRTL = lang === 'ar';
  const toc = generateTocFromSections(sections, lang);

  // Set document attributes for RTL
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    
    // Set meta tags
    document.title = metaTitle;
    
    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', metaDescription);

    return () => {
      // Reset to LTR when component unmounts (for other pages)
      document.documentElement.dir = 'ltr';
    };
  }, [lang, isRTL, metaTitle, metaDescription]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'text-right' : 'text-left'}`}>
      {/* Header */}
      <header className="px-4 py-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <a href="/">
            <img 
              src="/Vitalo_no_Background-removebg-preview.png" 
              alt="Vitalo" 
              className="h-12 md:h-16 w-auto"
            />
          </a>
          <LanguageToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 max-w-7xl mx-auto">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          {/* Table of Contents - Sticky on large screens */}
          <div className={`lg:col-span-1 mb-8 lg:mb-0 ${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
            <div className="lg:sticky lg:top-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  {tocTitle}
                </h3>
                <nav className="space-y-2">
                  {toc.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-sm text-gray-600 hover:text-teal-500 transition-colors duration-200 ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`lg:col-span-3 ${isRTL ? 'lg:order-1' : 'lg:order-2'}`}>
            <div className="max-w-3xl">
              {/* Title and Last Updated */}
              <div className="mb-8">
                <h1 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
                  {title}
                </h1>
                <p className="text-sm text-gray-500 mb-6">
                  {lastUpdatedText}: {formatLastUpdated(lang)}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {intro}
                </p>
              </div>

              {/* Sections */}
              <div className="space-y-12">
                {sections.map((section, index) => (
                  <section key={index} id={slugify(section.heading)}>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-4 scroll-mt-8">
                      {section.heading}
                    </h2>
                    <div className="space-y-4">
                      {section.body.map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-gray-700 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-12 border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-6">
            <a 
              href={lang === 'ar' ? '/ar/privacy-policy' : '/privacy-policy'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.privacyPolicy}
            </a>
            <span className="hidden sm:block text-gray-300">|</span>
            <a
              href={lang === 'ar' ? '/ar/terms' : '/terms'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.terms}
            </a>
            <span className="hidden sm:block text-gray-300">|</span>
            <a
              href={lang === 'ar' ? '/ar/contact' : '/contact'}
              className="text-gray-600 hover:text-teal-500 transition-colors duration-200"
            >
              {translations.footer.contact}
            </a>
          </div>
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Vitalo. {translations.footer.rights}
          </p>
        </div>
      </footer>
    </div>
  );
}